/**
 * #816 — which status chips give way when the ItemDetail status row runs out of width.
 *
 * The row must stay ONE line. The owner's screenshot shows it wrapping to two on a
 * phone, which pushes everything below it down and is the whole complaint.
 *
 * The RULE lives here as a pure function so it can be tested without a browser, a
 * ResizeObserver or a layout pass. The component measures; this decides. Same split as
 * itemSequence.js — measurement is environment, priority is policy.
 */

/**
 * Demotion order, least important first. From the approved mockup v6.
 * The Approve button and the save dot are deliberately ABSENT: they never hide, because
 * one is the only way to act on the item and the other is the only signal that an edit
 * is unsaved.
 */
// #816's own words: "the item type could be hidden, the 'approval' chip could be hidden
// (but the approve/unapproved button should always be displayed)". The approval badge is
// LAST because it is the most meaningful of the four — it says whether a human has signed
// this item off — so it goes only when everything else already has. The mockup fixed the
// first three; this appends the fourth the issue asks for.
//
// The Approve BUTTON and the save dot are absent from this list by construction, which is
// what makes "they never hide" a property of the data rather than a rule to remember.
export const DEMOTE_ORDER = [
  'item-type', 'record-verification', 'verification-badge', 'approval-badge',
];

/**
 * @param {number} available  px the chips may occupy on one line
 * @param {Object<string, number>} widths  measured px per chip key (missing = absent)
 * @param {string[]} [order]  demotion order, least important first
 * @returns {string[]} chip keys to HIDE
 *
 * Hides whole chips rather than clipping them: a clipped chip reads as a different,
 * shorter value, which is worse than an absent one. Stops as soon as the row fits, so
 * nothing hides unnecessarily — and if hiding every demotable chip still does not fit,
 * it returns them all rather than looping or clipping the ones that must stay.
 */
export function chipsToHide(available, widths, order = DEMOTE_ORDER) {
  const present = Object.keys(widths || {});
  const total = present.reduce((sum, k) => sum + (widths[k] || 0), 0);
  if (!Number.isFinite(available) || available <= 0) return [];   // unmeasured: hide nothing
  if (total <= available) return [];

  const hidden = [];
  let running = total;
  for (const key of order) {
    if (running <= available) break;
    if (!(key in widths)) continue;   // chip not rendered for this item
    hidden.push(key);
    running -= widths[key] || 0;
  }
  return hidden;
}

export default chipsToHide;
